<script setup lang="ts">
import { Loader2, Pencil, Plus, Save, ShieldX, Trash2 } from 'lucide-vue-next';
import { computed, ref, watch } from 'vue';

import ConfirmDialog from '@/components/ConfirmDialog.vue';
import { Button } from '@/components/ui/button';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogHeader,
    DialogTitle
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow
} from '@/components/ui/table';
import {
    createQuota,
    deleteQuota,
    getQuotas,
    type Quota,
    updateQuota
} from '@/services/quotas';
import { useTenantStore } from '@/stores/tenant';

type FormState = {
    name: string;
    value_usd: number;
};

const tenantStore = useTenantStore();

const quotas = ref<Quota[]>([]);
const loading = ref(false);
const saving = ref(false);
const error = ref<string | null>(null);
const formError = ref<string | null>(null);

const isFormDialogOpen = ref(false);
const isDeleteDialogOpen = ref(false);
const editingQuota = ref<Quota | null>(null);
const quotaToDelete = ref<Quota | null>(null);

const form = ref<FormState>(emptyForm());

const canManage = computed(
    () => tenantStore.tenantElqano && tenantStore.tenantAdmin
);
const isEditMode = computed(() => editingQuota.value !== null);
const tenantId = computed(() => tenantStore.tenantId || '');

const isFormValid = computed(() => {
    if (!form.value.name.trim()) return false;
    if (Number.isNaN(form.value.value_usd)) return false;
    if (form.value.value_usd < 0) return false;
    return true;
});

function emptyForm(): FormState {
    return {
        name: '',
        value_usd: 0
    };
}

function resetForm(quota: Quota | null = null) {
    formError.value = null;
    if (!quota) {
        form.value = emptyForm();
        return;
    }
    form.value = {
        name: quota.name,
        value_usd: quota.value_usd
    };
}

async function loadQuotas() {
    const currentTenantId = tenantId.value;
    if (!currentTenantId || !canManage.value) return;

    loading.value = true;
    error.value = null;
    try {
        quotas.value = await getQuotas(currentTenantId);
    } catch (err) {
        console.error(err);
        error.value = 'Failed to load quotas.';
    } finally {
        loading.value = false;
    }
}

function openCreateDialog() {
    editingQuota.value = null;
    resetForm();
    isFormDialogOpen.value = true;
}

function openEditDialog(quota: Quota) {
    editingQuota.value = quota;
    resetForm(quota);
    isFormDialogOpen.value = true;
}

function openDeleteDialog(quota: Quota) {
    quotaToDelete.value = quota;
    isDeleteDialogOpen.value = true;
}

async function submitForm() {
    if (!isFormValid.value || !tenantId.value) return;

    saving.value = true;
    formError.value = null;
    try {
        const payload = {
            name: form.value.name.trim(),
            value_usd: form.value.value_usd
        };

        if (isEditMode.value && editingQuota.value) {
            await updateQuota(tenantId.value, editingQuota.value.id, payload);
        } else {
            await createQuota(tenantId.value, payload);
        }

        isFormDialogOpen.value = false;
        await loadQuotas();
    } catch (err: unknown) {
        console.error(err);
        if (err instanceof Error && err.message === 'Quota already exists') {
            formError.value = 'A quota with this name already exists.';
        } else if (
            err instanceof Error &&
            err.message === 'Quota name is required'
        ) {
            formError.value = 'Quota name is required.';
        } else {
            formError.value = 'Failed to save quota.';
        }
    } finally {
        saving.value = false;
    }
}

async function confirmDelete() {
    if (!tenantId.value || !quotaToDelete.value) return;
    error.value = null;
    try {
        await deleteQuota(tenantId.value, quotaToDelete.value.id);
        quotaToDelete.value = null;
        await loadQuotas();
    } catch (err) {
        console.error(err);
        error.value = 'Failed to delete quota.';
    }
}

watch(
    () => [
        tenantStore.tenantId,
        tenantStore.tenantElqano,
        tenantStore.tenantAdmin
    ],
    () => {
        loadQuotas();
    },
    { immediate: true }
);
</script>

<template>
    <div class="title flex flex-col gap-6">
        <div class="flex items-start justify-between gap-4">
            <div>
                <h2 class="text-xl font-semibold">Quota management</h2>
                <p class="text-sm text-muted-foreground mt-1">
                    Manage global quota lookup keys and USD limits.
                </p>
            </div>
            <Button v-if="canManage" @click="openCreateDialog">
                <Plus class="h-4 w-4 mr-2" />
                Add Quota
            </Button>
        </div>

        <div
            v-if="!canManage"
            class="border rounded-xl p-6 flex items-center gap-3 text-muted-foreground"
        >
            <ShieldX class="h-5 w-5" />
            This section is available only to Elqano tenant admins.
        </div>

        <div
            v-else-if="error"
            class="p-3 bg-destructive/10 border border-destructive/20 rounded-lg"
        >
            <p class="text-sm text-destructive">{{ error }}</p>
        </div>

        <div v-if="canManage" class="border rounded-xl overflow-hidden">
            <div
                v-if="loading"
                class="w-full py-14 flex items-center justify-center text-muted-foreground"
            >
                <Loader2 class="animate-spin h-5 w-5 mr-2" />
                Loading quotas...
            </div>

            <Table v-else>
                <TableHeader>
                    <TableRow>
                        <TableHead>Name</TableHead>
                        <TableHead>Value (USD)</TableHead>
                        <TableHead class="text-right">Actions</TableHead>
                    </TableRow>
                </TableHeader>
                <TableBody>
                    <TableRow v-for="quota in quotas" :key="quota.id">
                        <TableCell class="font-medium">
                            {{ quota.name }}
                        </TableCell>
                        <TableCell>{{ quota.value_usd }}</TableCell>
                        <TableCell class="text-right">
                            <div class="inline-flex gap-2">
                                <Button
                                    variant="outline"
                                    size="icon-sm"
                                    @click="openEditDialog(quota)"
                                >
                                    <Pencil class="h-4 w-4" />
                                </Button>
                                <Button
                                    variant="destructive"
                                    size="icon-sm"
                                    @click="openDeleteDialog(quota)"
                                >
                                    <Trash2 class="h-4 w-4" />
                                </Button>
                            </div>
                        </TableCell>
                    </TableRow>
                    <TableRow v-if="quotas.length === 0">
                        <TableCell
                            :colspan="3"
                            class="text-center text-muted-foreground py-10"
                        >
                            No quotas configured yet.
                        </TableCell>
                    </TableRow>
                </TableBody>
            </Table>
        </div>
    </div>

    <Dialog v-model:open="isFormDialogOpen">
        <DialogContent class="max-w-lg">
            <DialogHeader>
                <DialogTitle>
                    {{ isEditMode ? 'Edit Quota' : 'Create Quota' }}
                </DialogTitle>
                <DialogDescription>
                    Set a unique name and monthly USD limit.
                </DialogDescription>
            </DialogHeader>

            <form class="space-y-4" @submit.prevent="submitForm">
                <div
                    v-if="formError"
                    class="p-3 bg-destructive/10 border border-destructive/20 rounded-lg"
                >
                    <p class="text-sm text-destructive">{{ formError }}</p>
                </div>

                <div class="space-y-2">
                    <Label for="quota_name">Name</Label>
                    <Input
                        id="quota_name"
                        v-model="form.name"
                        placeholder="e.g. stripe"
                        required
                    />
                </div>
                <div class="space-y-2">
                    <Label for="quota_value_usd">Value (USD)</Label>
                    <Input
                        id="quota_value_usd"
                        v-model.number="form.value_usd"
                        type="number"
                        min="0"
                        step="1"
                        required
                    />
                </div>

                <div class="flex justify-end gap-3">
                    <Button
                        type="button"
                        variant="secondary"
                        @click="isFormDialogOpen = false"
                    >
                        Cancel
                    </Button>
                    <Button type="submit" :disabled="saving || !isFormValid">
                        <Loader2
                            v-if="saving"
                            class="h-4 w-4 mr-2 animate-spin"
                        />
                        <Save v-else class="h-4 w-4 mr-2" />
                        {{ isEditMode ? 'Save changes' : 'Create quota' }}
                    </Button>
                </div>
            </form>
        </DialogContent>
    </Dialog>

    <ConfirmDialog
        v-model:is-confirm-dialog-open="isDeleteDialogOpen"
        title="Delete Quota"
        :description="`Are you sure you want to delete ${quotaToDelete?.name}?`"
        confirm-button-text="Delete"
        @confirm="confirmDelete"
    />
</template>
